<script lang="ts">
import { ref, computed, defineComponent, watch } from 'vue';
import { useAlerts } from '@/plugins/alerts';
import { useLocalization } from '@/plugins/localization';
import axios from 'axios';

export default defineComponent({
    name: 'FavoriteStar',
    props: {
        publicId: {
            type: String,
            required: true
        },
        isFavorite: {
            type: Boolean,
            required: true
        }
    },
    emits: ['update:favorite'],
    setup(props, { emit })
    {
        const { $alert } = useAlerts();
        const { $t } = useLocalization();

        const isLoading = ref(false);
        const internalIsFavorite = ref(props.isFavorite);

        // Watch for prop changes to sync internal state
        watch(() => props.isFavorite, (newValue) =>
        {
            internalIsFavorite.value = newValue;
        });

        const starIcon = computed(() =>
        {
            return internalIsFavorite.value ? 'fas fa-star' : 'far fa-star';
        });

        const starColor = computed(() =>
        {
            return internalIsFavorite.value ? 'text-warning' : 'text-muted';
        });

        const tooltipText = computed(() =>
        {
            return internalIsFavorite.value
                ? $t('[[[Usuń z ulubionych]]]')
                : $t('[[[Dodaj do ulubionych]]]');
        });

        const toggleFavorite = async () =>
        {
            if (isLoading.value) return;

            try
            {
                isLoading.value = true;

                const response = await axios.post(`documents/${props.publicId}/toggle-favorite`, {
                    isFavorite: !internalIsFavorite.value
                });

                // Update internal state immediately after successful API call
                internalIsFavorite.value = !internalIsFavorite.value;
                emit('update:favorite', internalIsFavorite.value);

                const message = internalIsFavorite.value
                    ? $t('[[[Dodano do ulubionych]]]')
                    : $t('[[[Usunięto z ulubionych]]]');

                $alert.success(message);
            }
            catch (error)
            {
                $alert.error($t('[[[Wystąpił błąd podczas aktualizacji ulubionych]]]'));
            }
            finally
            {
                isLoading.value = false;
            }
        };

        return {
            isLoading,
            starIcon,
            starColor,
            tooltipText,
            toggleFavorite
        };
    }
});
</script>

<template>
    <button
        type="button"
        class="btn btn-link p-2 me-1"
        :class="{ 'disabled': isLoading }"
        :disabled="isLoading"
        :title="tooltipText"
        @click="toggleFavorite"
    >
        <i
            :class="[starIcon, starColor, 'fs-2']"
            :style="{ opacity: isLoading ? 0.6 : 1 }"
        ></i>
    </button>
</template>

<style lang="scss" scoped>
.btn-link {
    text-decoration: none;
    border: none;
    background: transparent;

    &:hover {
        text-decoration: none;
        background: transparent;
    }

    &:focus {
        box-shadow: none;
        background: transparent;
    }
}

.disabled {
    pointer-events: none;
}
</style>
